import { useEffect, useState, useMemo, useRef, Component, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Edit, Trash2, Plus, Eye, EyeOff, Trash, BookOpen, FileText,
  BarChart2, MoreVertical, Calendar, Send, X, TrendingUp, CheckCheck, ScanEye, Zap,
} from 'lucide-react';
import { fetchPosts, deletePost, updatePost, transitionPost, makePostDynamic, fetchPostViews, Post } from '../lib/api';
import PostViewPanel from '../components/PostViewPanel';
import PostPreviewPanel from '../components/PostPreviewPanel';

class PanelErrorBoundary extends Component<{ onClose: () => void; children: ReactNode }, { error: Error | null }> {
  state = { error: null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <>
          <div className="fixed inset-0 bg-black/40 z-[200]" onClick={this.props.onClose} />
          <div className="fixed inset-y-0 right-0 z-[201] w-full max-w-2xl flex flex-col items-center justify-center bg-white dark:bg-gray-900 p-8 gap-4">
            <p className="text-red-500 font-semibold text-lg">Panel crashed</p>
            <pre className="text-xs text-gray-400 whitespace-pre-wrap max-w-full overflow-auto">{String(this.state.error)}</pre>
            <button onClick={this.props.onClose} className="px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded-lg text-sm">Close</button>
          </div>
        </>
      );
    }
    return this.props.children;
  }
}

const LANG_LABELS: Record<string, string> = {
  en: '🇬🇧 EN', tr: '🇹🇷 TR', de: '🇩🇪 DE', fr: '🇫🇷 FR',
  id: '🇮🇩 ID', pl: '🇵🇱 PL', pt: '🇧🇷 PT', ar: '🇸🇦 AR',
  hi: '🇮🇳 HI', ur: '🇵🇰 UR',
};
const LANGUAGES = Object.entries(LANG_LABELS).map(([v, l]) => ({ value: v, label: l }));
const PAGE_SIZE = 20;
const TYPES = [
  { key: 'all',   label: 'All',    icon: null },
  { key: 'blog',  label: 'Blog',   icon: FileText },
  { key: 'guide', label: 'Guides', icon: BookOpen },
] as const;

const STATUS_OPTIONS = [
  { value: 'all',       label: 'All' },
  { value: 'published', label: 'Published' },
  { value: 'draft',     label: 'Draft' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'disabled',  label: 'Unpublished' },
];

const STATUS_STYLES: Record<string, string> = {
  published: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  draft:     'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  scheduled: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  disabled:  'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

const STATUS_LABEL: Record<string, string> = {
  published: 'Published',
  draft: 'Draft',
  scheduled: 'Scheduled',
  disabled: 'Unpublished',
};

const inputCls = 'px-3 py-2 rounded-xl border border-gray-300 bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm';

// ── Action dropdown ──────────────────────────────────────────────────────────
interface ActionMenuProps {
  post: Post;
  onClose: () => void;
  onView: () => void;
  onPreview: () => void;
  onDelete: () => void;
  onStatusChange: (s: Post['status']) => void;
  onScheduleRequest: () => void;
  onMakeDynamic: () => void;
  baseRoute?: string;
}

function ActionMenu({ post, onClose, onView, onPreview, onDelete, onStatusChange, onScheduleRequest, onMakeDynamic, baseRoute = '/blog' }: ActionMenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) onClose(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [onClose]);

  const s = post.status;
  const Item = ({ icon: Icon, label, onClick, cls = '' }: { icon: React.ElementType; label: string; onClick: () => void; cls?: string }) => (
    <button onClick={() => { onClick(); onClose(); }}
      className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700/50 transition text-left ${cls}`}>
      <Icon size={14} className="flex-shrink-0" />{label}
    </button>
  );

  return (
    <div ref={ref}
      className="absolute right-0 top-full mt-1 z-50 w-52 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl overflow-hidden">
      <Item icon={ScanEye} label="Preview"           onClick={onPreview}                         cls="text-violet-600 dark:text-violet-400" />
      <Item icon={Eye}     label="View details"     onClick={onView}                            cls="text-gray-700 dark:text-gray-200" />
      <Link to={`${baseRoute}/edit/${post.id}`} onClick={onClose}
        className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition">
        <Edit size={14} className="flex-shrink-0" />Edit
      </Link>
      <div className="border-t border-gray-100 dark:border-gray-700 my-1" />
      {s === 'published' && <Item icon={EyeOff}   label="Unpublish → Draft"  onClick={() => onStatusChange('draft')}     cls="text-amber-600 dark:text-amber-400" />}
      {s === 'draft'     && <>
        <Item icon={Eye}      label="Publish now"        onClick={() => onStatusChange('published')} cls="text-emerald-600 dark:text-emerald-400" />
        <Item icon={Calendar} label="Schedule…"          onClick={onScheduleRequest}                 cls="text-blue-600 dark:text-blue-400" />
      </>}
      {s === 'scheduled' && <>
        <Item icon={Send}   label="Publish now"        onClick={() => onStatusChange('published')} cls="text-emerald-600 dark:text-emerald-400" />
        <Item icon={EyeOff} label="Back to Draft"      onClick={() => onStatusChange('draft')}     cls="text-amber-600 dark:text-amber-400" />
      </>}
      {s === 'disabled'  && <Item icon={FileText} label="Move to Draft"      onClick={() => onStatusChange('draft')}     cls="text-gray-600 dark:text-gray-300" />}
      {post.post_type !== 'guide' && !post.is_dynamic && (
        <Item icon={Zap} label="Move to dynamic" onClick={onMakeDynamic} cls="text-cyan-600 dark:text-cyan-400" />
      )}
      <div className="border-t border-gray-100 dark:border-gray-700 my-1" />
      <Item icon={Trash2} label="Delete" onClick={onDelete} cls="text-red-600 dark:text-red-400" />
    </div>
  );
}

// ── Schedule modal ───────────────────────────────────────────────────────────
function ScheduleModal({ post, onClose, onScheduled }: { post: Post; onClose: () => void; onScheduled: () => void }) {
  const [datetime, setDatetime] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const submit = async () => {
    if (!datetime) { setError('Pick a date and time.'); return; }
    const ts = Math.floor(new Date(datetime).getTime() / 1000);
    if (ts <= Date.now() / 1000) { setError('Must be in the future.'); return; }
    setSaving(true);
    const res = await transitionPost(post.id, 'schedule', ts);
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      setError((data as { error?: string }).error || `Could not schedule (${res.status}).`);
      setSaving(false);
      return;
    }
    onScheduled(); onClose();
  };
  return (
    <>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div className="pointer-events-auto w-full max-w-sm bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Calendar size={16} className="text-blue-500" /> Schedule Post
            </h3>
            <button onClick={onClose} className="p-1 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"><X size={16} /></button>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-4 leading-relaxed">
            Schedule <span className="font-medium text-gray-700 dark:text-gray-200">"{post.title}"</span>
          </p>
          <input type="datetime-local" value={datetime}
            onChange={e => { setDatetime(e.target.value); setError(''); }}
            className="w-full px-3 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2" />
          {error && <p className="text-xs text-red-500 mb-2">{error}</p>}
          <div className="flex gap-2 mt-4">
            <button onClick={onClose} className="flex-1 px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition">Cancel</button>
            <button onClick={submit} disabled={saving}
              className="flex-1 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition flex items-center justify-center gap-1.5">
              <Calendar size={14} />{saving ? 'Scheduling…' : 'Schedule'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Mobile post card ─────────────────────────────────────────────────────────
interface CardProps {
  post: Post;
  views?: number;
  selected: boolean;
  onToggle: () => void;
  onView: () => void;
  onPreview: () => void;
  onDelete: () => void;
  onStatusChange: (s: Post['status']) => void;
  onScheduleRequest: () => void;
  onMakeDynamic: () => void;
  menuOpen: boolean;
  onMenuToggle: () => void;
  onMenuClose: () => void;
  baseRoute?: string;
}

function PostCard({ post, views, selected, onToggle, onView, onPreview, onDelete, onStatusChange, onScheduleRequest, onMakeDynamic, menuOpen, onMenuToggle, onMenuClose, baseRoute = '/blog' }: CardProps) {
  const ptype = post.post_type || 'blog';
  const s = post.status;
  return (
    <div className={`relative p-4 border-b border-gray-200/60 dark:border-gray-700/60 last:border-0 transition ${selected ? 'bg-blue-50/50 dark:bg-blue-900/10' : 'hover:bg-gray-50/50 dark:hover:bg-gray-700/20'}`}>
      {/* Preview badge — upper right corner */}
      <button
        onClick={onPreview}
        className="absolute top-2.5 right-10 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 text-[10px] font-bold hover:bg-violet-200 dark:hover:bg-violet-900/60 transition z-10"
      >
        <ScanEye size={10} /> Preview
      </button>
      <div className="flex items-start gap-3">
        {/* Checkbox */}
        <input type="checkbox" checked={selected} onChange={onToggle} className="rounded mt-1 flex-shrink-0" />

        {/* Main content — clicking title/slug goes to that post's analytics */}
        <Link to={`${baseRoute}/analytics/${post.id}`} className="flex-1 min-w-0">
          {/* Badges */}
          <div className="flex items-center gap-1.5 flex-wrap mb-1">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${
              ptype === 'guide' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
            }`}>
              {ptype === 'guide' ? <BookOpen size={9} /> : <FileText size={9} />}{ptype}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_STYLES[s]}`}>
              {STATUS_LABEL[s] || s}
            </span>
            {ptype !== 'guide' && (
              <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                post.is_dynamic
                  ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
              }`}>
                {post.is_dynamic ? 'dynamic' : 'static'}
              </span>
            )}
            <span className="text-[11px] text-gray-400">{LANG_LABELS[post.lang] || post.lang}</span>
            {views != null && (
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[11px] font-semibold bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                <TrendingUp size={9} /> {views.toLocaleString()} views
              </span>
            )}
          </div>
          {/* Title */}
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-snug line-clamp-2 hover:text-purple-600 dark:hover:text-purple-400 transition">
            {post.title}
          </p>
          {/* Slug */}
          <p className="text-[10px] text-gray-400 font-mono mt-0.5 truncate">{post.slug}</p>
          {s === 'scheduled' && post.publish_at && (
            <p className="text-[10px] text-blue-500 mt-0.5">→ {new Date(post.publish_at * 1000).toLocaleString()}</p>
          )}
        </Link>

        {/* Action menu button */}
        <div className="relative flex-shrink-0">
          <button onClick={onMenuToggle}
            className={`p-2 rounded-lg transition ${menuOpen ? 'bg-gray-200 dark:bg-gray-600' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
            <MoreVertical size={16} />
          </button>
          {menuOpen && (
            <ActionMenu post={post} onClose={onMenuClose} onView={onView} onPreview={onPreview}
              onDelete={onDelete} onStatusChange={onStatusChange} onScheduleRequest={onScheduleRequest}
              onMakeDynamic={onMakeDynamic} baseRoute={baseRoute} />
          )}
        </div>
      </div>

      {/* Inline quick actions (always visible on mobile) */}
      <div className="flex items-center gap-2 mt-3 ml-7">
        <button onClick={onView}
          className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700/60 text-gray-700 dark:text-gray-300 text-xs font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition">
          <Eye size={13} /> View
        </button>
        <Link to={`${baseRoute}/edit/${post.id}`}
          className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs font-medium hover:bg-blue-200 dark:hover:bg-blue-900/50 transition">
          <Edit size={13} /> Edit
        </Link>
        {s === 'published' && (
          <button onClick={() => onStatusChange('draft')}
            className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-medium hover:bg-amber-200 dark:hover:bg-amber-900/50 transition">
            <EyeOff size={13} /> Unpublish
          </button>
        )}
        {(s === 'draft' || s === 'disabled') && (
          <button onClick={() => onStatusChange('published')}
            className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs font-medium hover:bg-emerald-200 dark:hover:bg-emerald-900/50 transition">
            <Eye size={13} /> Publish
          </button>
        )}
        {s === 'scheduled' && (
          <button onClick={() => onStatusChange('published')}
            className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs font-medium hover:bg-emerald-200 dark:hover:bg-emerald-900/50 transition">
            <Send size={13} /> Publish now
          </button>
        )}
        <button onClick={onDelete}
          className="p-1.5 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 transition">
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────
// ── Inline confirmation dialog (replaces window.confirm which is blocked on Android) ──
function ConfirmDialog({ message, onConfirm, onCancel }: { message: string; onConfirm: () => void; onCancel: () => void }) {
  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[400]" onClick={onCancel} />
      <div className="fixed inset-0 z-[401] flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 max-w-sm w-full">
          <p className="text-gray-800 dark:text-gray-100 text-sm font-medium mb-5">{message}</p>
          <div className="flex gap-3">
            <button onClick={onCancel}
              className="flex-1 px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition">
              Cancel
            </button>
            <button onClick={() => { onConfirm(); onCancel(); }}
              className="flex-1 px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition">
              Delete
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Toast notification ──────────────────────────────────────────────────────
function Toast({ msg, ok }: { msg: string; ok: boolean }) {
  return (
    <div className={`fixed bottom-5 left-1/2 -translate-x-1/2 z-[500] px-5 py-3 rounded-xl shadow-2xl text-white text-sm font-medium flex items-center gap-2 transition-all ${ok ? 'bg-emerald-600' : 'bg-red-600'}`}>
      {ok ? '✓' : '✕'} {msg}
    </div>
  );
}

export default function BlogManager({ defaultType = 'all', baseRoute = '/blog' }: { defaultType?: 'all'|'blog'|'guide'; baseRoute?: string } = {}) {
  const [posts, setPosts]               = useState<Post[]>([]);
  const [postViews, setPostViews]       = useState<Record<string, number>>({});
  const [typeFilter, setTypeFilter]     = useState<'all'|'blog'|'guide'>(defaultType);
  const [statusFilter, setStatusFilter] = useState('all');
  const [langFilter, setLangFilter]     = useState('all');
  const [search, setSearch]             = useState('');
  const [page, setPage]                 = useState(1);
  const [selected, setSelected]         = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting]   = useState(false);
  const [bulkPublishing, setBulkPublishing] = useState(false);
  const [loading, setLoading]           = useState(false);
  const [viewPost, setViewPost]         = useState<Post | null>(null);
  const [previewPost, setPreviewPost]   = useState<Post | null>(null);
  const [menuOpen, setMenuOpen]         = useState<string | null>(null);
  const [schedulePost, setSchedulePost] = useState<Post | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [bulkConfirm, setBulkConfirm]   = useState<null | { action: 'delete' | 'publish' | 'unpublish' | 'dynamic'; count: number }>(null);
  const [bulkMoving, setBulkMoving]     = useState(false);
  const [moveProgress, setMoveProgress] = useState(0);
  const [busyId, setBusyId]             = useState<string | null>(null);
  const [toast, setToast]               = useState<{ msg: string; ok: boolean } | null>(null);

  const showToast = (msg: string, ok: boolean) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  };

  const load = async () => {
    setLoading(true);
    const [result, views] = await Promise.all([
      fetchPosts({ status: statusFilter, lang: langFilter }),
      fetchPostViews(),
    ]);
    setPosts(result);
    setPostViews(views);
    setPage(1);
    setSelected(new Set());
    setLoading(false);
  };
  useEffect(() => { load(); }, [statusFilter, langFilter]);

  const remove = async (id: string) => {
    setBusyId(id);
    try {
      const res = await deletePost(id);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(`Delete failed (${res.status}) — ${(data as any).error || 'try again'}`);
      }
      showToast('Post deleted', true);
      load();
    } catch (e) {
      showToast((e as Error).message || 'Delete failed — try again', false);
    } finally {
      setBusyId(null);
    }
  };

  const changeStatus = async (id: string, newStatus: Post['status']) => {
    setBusyId(id);
    try {
      // Transitions clear/set publish_at correctly: "publish now" on a
      // scheduled post must not keep the future timestamp.
      const action = newStatus === 'published' ? 'publish' : newStatus === 'disabled' ? 'disable' : 'draft';
      let res = await transitionPost(id, action);
      if (res.status === 404) res = await updatePost(id, { status: newStatus });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(`Action failed (${res.status}) — ${(data as any).error || 'try again'}`);
      }
      const label = newStatus === 'published' ? 'Published ✓' : newStatus === 'draft' ? 'Moved to Draft ✓' : `Status: ${newStatus}`;
      showToast(label, true);
      load();
    } catch (e) {
      showToast((e as Error).message || 'Action failed — try again', false);
    } finally {
      setBusyId(null);
    }
  };

  // One-by-one cutover. Each call flips one post to D1-rendered, bumps its
  // updated_at and pings IndexNow, so every crawler refetches that URL.
  const makeDynamic = async (id: string) => {
    setBusyId(id);
    try {
      const res = await makePostDynamic(id);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(`Move failed (${res.status}) — ${(data as { error?: string }).error || 'try again'}`);
      }
      showToast('Moved to dynamic ✓ — crawlers notified', true);
      load();
    } catch (e) {
      showToast((e as Error).message || 'Move failed — try again', false);
    } finally {
      setBusyId(null);
    }
  };

  const filtered = useMemo(() => {
    let r = posts;
    if (typeFilter !== 'all') r = r.filter(p => p.post_type === typeFilter || (!p.post_type && typeFilter === 'blog'));
    const q = search.trim().toLowerCase();
    if (q) r = r.filter(p => p.title.toLowerCase().includes(q) || p.slug.toLowerCase().includes(q));
    return r;
  }, [posts, typeFilter, search]);

  const totalPages  = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginated   = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const pageIds     = paginated.map(p => p.id);
  const allSelected = pageIds.length > 0 && pageIds.every(id => selected.has(id));

  const toggleSelect    = (id: string) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = () => {
    if (allSelected) setSelected(prev => { const n = new Set(prev); pageIds.forEach(id => n.delete(id)); return n; });
    else             setSelected(prev => { const n = new Set(prev); pageIds.forEach(id => n.add(id)); return n; });
  };

  const executeBulkDelete = async () => {
    setBulkDeleting(true);
    await Promise.all(Array.from(selected).map(id => deletePost(id)));
    setBulkDeleting(false); load();
  };

  const executeBulkPublish = async () => {
    setBulkPublishing(true);
    const now = Math.floor(Date.now() / 1000);
    await Promise.all(
      Array.from(selected).map(id => updatePost(id, { status: 'published', publish_at: now }))
    );
    setBulkPublishing(false); load();
  };

  const executeBulkUnpublish = async () => {
    setBulkPublishing(true);
    await Promise.all(
      Array.from(selected).map(id => updatePost(id, { status: 'draft' }))
    );
    setBulkPublishing(false); load();
  };

  // Bulk cutover: move every selected blog post (guides excluded) to the
  // D1-rendered pipeline. Runs one-by-one with progress — each call bumps
  // updated_at and pings IndexNow for that slug.
  const executeBulkDynamic = async (ids: string[]) => {
    const targets = ids.filter(Boolean);
    if (!targets.length) return;
    setBulkMoving(true);
    setMoveProgress(0);
    let ok = 0;
    let failed = 0;
    for (let i = 0; i < targets.length; i++) {
      try {
        const res = await makePostDynamic(targets[i]);
        if (res.ok) ok++; else failed++;
      } catch { failed++; }
      setMoveProgress(i + 1);
    }
    setBulkMoving(false);
    showToast(`Moved ${ok} post(s) to dynamic${failed ? `, ${failed} failed` : ''} — crawlers notified`, failed === 0);
    load();
  };

  const bulkDynamic = () => {
    // Only blog posts that are not dynamic yet can move.
    const ids = posts
      .filter(p => selected.has(p.id) && (!p.post_type || p.post_type === 'blog') && !p.is_dynamic)
      .map(p => p.id);
    if (!ids.length) { showToast('No static blog posts in the selection', false); return; }
    setBulkConfirm({ action: 'dynamic', count: ids.length });
  };

  const dynamicTargets = (action: 'dynamic') => {
    return posts.filter(p => selected.has(p.id) && (!p.post_type || p.post_type === 'blog') && !p.is_dynamic).map(p => p.id);
  };

  const bulkDelete    = () => { if (selected.size) setBulkConfirm({ action: 'delete',    count: selected.size }); };
  const bulkPublish   = () => { if (selected.size) setBulkConfirm({ action: 'publish',   count: selected.size }); };
  const bulkUnpublish = () => { if (selected.size) setBulkConfirm({ action: 'unpublish', count: selected.size }); };

  const blogCount  = posts.filter(p => !p.post_type || p.post_type === 'blog').length;
  const guideCount = posts.filter(p => p.post_type === 'guide').length;

  const statusCounts = useMemo(() => {
    const base = typeFilter === 'all' ? posts : posts.filter(p =>
      p.post_type === typeFilter || (!p.post_type && typeFilter === 'blog'));
    return {
      published: base.filter(p => p.status === 'published').length,
      draft:     base.filter(p => p.status === 'draft').length,
      scheduled: base.filter(p => p.status === 'scheduled').length,
      disabled:  base.filter(p => p.status === 'disabled').length,
    };
  }, [posts, typeFilter]);

  return (
    <div>
      {/* Panels / modals */}
      {viewPost && (
        <PanelErrorBoundary onClose={() => setViewPost(null)}>
          <PostViewPanel post={viewPost} onClose={() => setViewPost(null)} onRefresh={load}
            onScheduleRequest={(p) => { setSchedulePost(p); setViewPost(null); }} />
        </PanelErrorBoundary>
      )}
      {previewPost && (
        <PostPreviewPanel post={previewPost} onClose={() => setPreviewPost(null)} />
      )}
      {schedulePost && (
        <ScheduleModal post={schedulePost} onClose={() => setSchedulePost(null)} onScheduled={load} />
      )}

      {/* Single-post delete confirm */}
      {confirmDeleteId && (
        <ConfirmDialog
          message="This post will be permanently deleted. Are you sure?"
          onConfirm={() => { remove(confirmDeleteId); setConfirmDeleteId(null); }}
          onCancel={() => setConfirmDeleteId(null)}
        />
      )}

      {/* Bulk action confirm */}
      {bulkConfirm && (
        <ConfirmDialog
          message={
            bulkConfirm.action === 'delete'
              ? `${bulkConfirm.count} post(s) will be permanently deleted. Are you sure?`
              : bulkConfirm.action === 'publish'
              ? `${bulkConfirm.count} post(s) will be published. Are you sure?`
              : bulkConfirm.action === 'unpublish'
              ? `${bulkConfirm.count} post(s) will be unpublished and moved to Draft. Are you sure?`
              : `${bulkConfirm.count} blog post(s) will be moved to the dynamic pipeline: served from D1 at request time, removed from the static build, and submitted to IndexNow. URLs and SEO stay identical. Continue?`
          }
          onConfirm={() => {
            const action = bulkConfirm.action;
            if (action === 'delete')    executeBulkDelete();
            if (action === 'publish')   executeBulkPublish();
            if (action === 'unpublish') executeBulkUnpublish();
            if (action === 'dynamic') executeBulkDynamic(dynamicTargets(action));
            setBulkConfirm(null);
          }}
          onCancel={() => setBulkConfirm(null)}
        />
      )}

      {/* Toast */}
      {toast && <Toast msg={toast.msg} ok={toast.ok} />}

      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            {defaultType === 'guide' ? 'Guides' : 'Blog & Guides'}
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {defaultType === 'guide' ? `${guideCount} guides` : `${blogCount} blog posts · ${guideCount} guides`}
          </p>
        </div>
        <Link to={`${baseRoute}/new`}
          className="inline-flex items-center gap-1.5 px-4 py-2 sm:px-5 sm:py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 shadow-lg transition-all text-sm font-medium">
          <Plus size={15} /> <span className="hidden sm:inline">{defaultType === 'guide' ? 'New Guide' : 'New Post'}</span><span className="sm:hidden">New</span>
        </Link>
      </div>

      {/* Type tabs — hidden when manager is locked to a specific type */}
      {defaultType === 'all' && (
        <div className="flex gap-1 mb-4 bg-gray-100 dark:bg-gray-800/60 p-1 rounded-xl w-fit">
          {TYPES.map(t => (
            <button key={t.key} onClick={() => { setTypeFilter(t.key); setPage(1); }}
              className={`flex items-center gap-1 px-3 py-1.5 sm:px-4 rounded-lg text-xs sm:text-sm font-medium transition ${typeFilter === t.key ? 'bg-white dark:bg-gray-700 shadow text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'}`}>
              {t.icon && <t.icon size={13} />} {t.label}
              {t.key === 'blog'  && <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-[10px] font-bold px-1.5 py-0.5 rounded-full">{blogCount}</span>}
              {t.key === 'guide' && <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-[10px] font-bold px-1.5 py-0.5 rounded-full">{guideCount}</span>}
            </button>
          ))}
        </div>
      )}

      {/* Status filter tabs */}
      <div className="flex gap-1.5 mb-4 flex-wrap">
        {STATUS_OPTIONS.map(opt => {
          const count = opt.value === 'all' ? filtered.length : (statusCounts as Record<string, number>)[opt.value] ?? 0;
          const active = statusFilter === opt.value;
          const colors: Record<string, string> = {
            published: active ? 'bg-emerald-600 text-white' : 'text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800 hover:bg-emerald-50 dark:hover:bg-emerald-900/20',
            draft:     active ? 'bg-gray-600 text-white'    : 'text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800',
            scheduled: active ? 'bg-blue-600 text-white'    : 'text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800 hover:bg-blue-50 dark:hover:bg-blue-900/20',
            disabled:  active ? 'bg-red-600 text-white'     : 'text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20',
            all:       active ? 'bg-blue-600 text-white'    : 'text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800',
          };
          return (
            <button key={opt.value} onClick={() => { setStatusFilter(opt.value); setPage(1); }}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold transition ${colors[opt.value]}`}>
              {opt.label}
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${active ? 'bg-white/20' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Search + lang + bulk delete */}
      <div className="mb-4 flex gap-2 flex-wrap items-center">
        <input type="text" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search title or slug…"
          className="flex-1 min-w-0 px-3 py-2 rounded-xl border border-gray-300 bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm" />
        <select value={langFilter} onChange={e => setLangFilter(e.target.value)} className={`${inputCls} w-auto`}>
          <option value="all">All langs</option>
          {LANGUAGES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
        </select>
        {selected.size > 0 && (
          <>
            <button onClick={bulkPublish} disabled={bulkPublishing || bulkDeleting}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 text-sm font-medium">
              <CheckCheck size={14} />{bulkPublishing ? 'Publishing…' : `Publish (${selected.size})`}
            </button>
            <button onClick={bulkUnpublish} disabled={bulkPublishing || bulkDeleting}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-gray-500 text-white rounded-xl hover:bg-gray-600 disabled:opacity-50 text-sm font-medium">
              <EyeOff size={14} />{bulkPublishing ? '…' : `Unpublish (${selected.size})`}
            </button>
            <button onClick={bulkDelete} disabled={bulkDeleting || bulkPublishing || bulkMoving}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50 text-sm font-medium">
              <Trash size={14} />{bulkDeleting ? 'Deleting…' : `Delete (${selected.size})`}
            </button>
            <button onClick={bulkDynamic} disabled={bulkMoving || bulkPublishing || bulkDeleting}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-cyan-600 text-white rounded-xl hover:bg-cyan-700 disabled:opacity-50 text-sm font-medium">
              <Zap size={14} />{bulkMoving ? `Moving… ${moveProgress}` : `Move to dynamic (${selected.size})`}
            </button>
          </>
        )}
      </div>

      {/* ── MOBILE: card list (hidden on md+) ── */}
      <div className="md:hidden bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-xl border border-gray-200/60 dark:border-gray-700/60 overflow-hidden">
        {/* Select all bar */}
        <div className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-200/60 dark:border-gray-700/60 bg-gray-50/80 dark:bg-gray-700/30">
          <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} className="rounded" />
          <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">
            {selected.size > 0 ? `${selected.size} selected` : `${filtered.length} posts`}
          </span>
        </div>

        {loading ? (
          <div className="py-10 text-center text-gray-400">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto mb-2" />Loading…
          </div>
        ) : paginated.length === 0 ? (
          <div className="py-12 text-center text-gray-400">
            <FileText size={36} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">No posts found.</p>
            <Link to={`${baseRoute}/new`} className="mt-2 inline-flex items-center gap-1 text-blue-600 text-sm font-medium"><Plus size={13} />Create one</Link>
          </div>
        ) : paginated.map(p => (
          <PostCard key={p.id} post={p}
            views={postViews[p.slug]}
            selected={selected.has(p.id)}
            onToggle={() => toggleSelect(p.id)}
            onView={() => setViewPost(p)}
            onPreview={() => setPreviewPost(p)}
            onDelete={() => setConfirmDeleteId(p.id)}
            onStatusChange={(s) => changeStatus(p.id, s)}
            onScheduleRequest={() => setSchedulePost(p)}
            onMakeDynamic={() => makeDynamic(p.id)}
            menuOpen={menuOpen === p.id}
            onMenuToggle={() => setMenuOpen(menuOpen === p.id ? null : p.id)}
            onMenuClose={() => setMenuOpen(null)}
            baseRoute={baseRoute}
          />
        ))}
      </div>

      {/* ── DESKTOP: full table (hidden on mobile) ── */}
      <div className="hidden md:block bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-xl border border-gray-200/60 dark:border-gray-700/60 overflow-hidden">
        {loading ? (
          <div className="py-12 text-center text-gray-400">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto mb-3" />Loading…
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50/80 dark:bg-gray-700/30">
                <tr>
                  <th className="p-4"><input type="checkbox" checked={allSelected} onChange={toggleSelectAll} className="rounded" /></th>
                  <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">Type</th>
                  <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">Title / Slug</th>
                  <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">Lang</th>
                  <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">Status</th>
                  <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">Views</th>
                  <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">Created / Updated</th>
                  <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">SEO</th>
                  <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map(p => {
                  const ptype = p.post_type || 'blog';
                  return (
                    <tr key={p.id} className="border-t border-gray-200/60 dark:border-gray-700/60 hover:bg-gray-50/50 dark:hover:bg-gray-700/20 transition">
                      <td className="p-4"><input type="checkbox" checked={selected.has(p.id)} onChange={() => toggleSelect(p.id)} className="rounded" /></td>
                      <td className="p-4">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
                          ptype === 'guide' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                        }`}>
                          {ptype === 'guide' ? <BookOpen size={11} /> : <FileText size={11} />}{ptype}
                        </span>
                      </td>
                      <td className="p-4 max-w-[200px]">
                        <Link to={`${baseRoute}/analytics/${p.id}`}
                          className="font-medium text-gray-900 dark:text-gray-100 hover:text-purple-600 dark:hover:text-purple-400 text-left truncate max-w-full transition block w-full">
                          {p.title}
                        </Link>
                        <p className="text-[10px] text-gray-400 mt-0.5 font-mono truncate">{p.slug}</p>
                      </td>
                      <td className="p-4 text-gray-600 dark:text-gray-400 text-xs">{LANG_LABELS[p.lang] || p.lang}</td>
                      <td className="p-4">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_STYLES[p.status]}`}>
                          {STATUS_LABEL[p.status] || p.status}
                        </span>
                        {ptype !== 'guide' && (
                          <span className={`ml-1 px-2 py-1 rounded-full text-[10px] font-semibold ${
                            p.is_dynamic
                              ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300'
                              : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                          }`}>
                            {p.is_dynamic ? 'dynamic' : 'static'}
                          </span>
                        )}
                        {p.status === 'scheduled' && p.publish_at && (
                          <div className="text-[10px] text-blue-500 mt-0.5">→ {new Date(p.publish_at * 1000).toLocaleDateString()}</div>
                        )}
                      </td>
                      <td className="p-4">
                        {postViews[p.slug] != null ? (
                          <Link to={`${baseRoute}/analytics/${p.id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 text-xs font-semibold hover:bg-purple-100 dark:hover:bg-purple-900/40 transition">
                            <TrendingUp size={11} /> {postViews[p.slug].toLocaleString()}
                          </Link>
                        ) : (
                          <span className="text-gray-300 dark:text-gray-600 text-xs">—</span>
                        )}
                      </td>
                      <td className="p-4 text-gray-500 dark:text-gray-400 text-xs">
                        <div>{new Date(p.created_at * 1000).toLocaleDateString()}</div>
                        {p.updated_at && p.updated_at !== p.created_at && (
                          <div className="text-[10px] text-gray-400">↻ {new Date(p.updated_at * 1000).toLocaleDateString()}</div>
                        )}
                      </td>
                      <td className="p-4 text-xs">
                        <div className="flex flex-col gap-1">
                          <span
                            title={p.is_dynamic || ptype === 'guide' ? 'Served from D1 at request time' : 'Prerendered at build time'}
                            className={`px-2 py-0.5 rounded-full text-[10px] font-semibold w-fit ${
                              p.is_dynamic || ptype !== 'guide'
                                ? 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300'
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {p.is_dynamic || ptype !== 'guide' ? 'dynamic / in sitemap' : 'static'}
                          </span>
                          <span
                            title="IndexNow ping (Bing/Yandex) for the live languages of this slug"
                            className={`text-[10px] ${p.indexnotified_at ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400'}`}
                          >
                            {p.indexnotified_at
                              ? `IndexNow ${new Date(p.indexnotified_at * 1000).toLocaleDateString()}`
                              : 'IndexNow —'}
                          </span>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-1">
                          {/* Preview */}
                          <button onClick={() => setPreviewPost(p)} title="Preview" className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400 hover:bg-violet-100 dark:hover:bg-violet-900/40 text-xs font-semibold transition">
                            <ScanEye size={13} /> Preview
                          </button>
                          {/* View */}
                          <button onClick={() => setViewPost(p)} title="View" className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition">
                            <Eye size={15} />
                          </button>
                          {/* Edit */}
                          <Link to={`${baseRoute}/edit/${p.id}`} title="Edit" className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 transition">
                            <Edit size={15} />
                          </Link>
                          {/* Status toggle */}
                          {p.status === 'published' && (
                            <button onClick={() => changeStatus(p.id, 'draft')} title="Unpublish"
                              className="p-1.5 rounded-lg text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition">
                              <EyeOff size={15} />
                            </button>
                          )}
                          {(p.status === 'draft' || p.status === 'disabled') && (
                            <button onClick={() => changeStatus(p.id, 'published')} title="Publish"
                              className="p-1.5 rounded-lg text-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition">
                              <Eye size={15} />
                            </button>
                          )}
                          {p.status === 'scheduled' && (
                            <button onClick={() => changeStatus(p.id, 'published')} title="Publish now"
                              className="p-1.5 rounded-lg text-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition">
                              <Send size={15} />
                            </button>
                          )}
                          {/* More dropdown */}
                          <div className="relative">
                            <button onClick={() => setMenuOpen(menuOpen === p.id ? null : p.id)}
                              className={`p-1.5 rounded-lg transition ${menuOpen === p.id ? 'bg-gray-100 dark:bg-gray-700' : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                              <MoreVertical size={15} />
                            </button>
                            {menuOpen === p.id && (
                              <ActionMenu post={p} onClose={() => setMenuOpen(null)}
                                onView={() => { setViewPost(p); setMenuOpen(null); }}
                                onPreview={() => { setPreviewPost(p); setMenuOpen(null); }}
                                onDelete={() => setConfirmDeleteId(p.id)}
                                onStatusChange={(s) => changeStatus(p.id, s)}
                                onScheduleRequest={() => { setSchedulePost(p); setMenuOpen(null); }}
                                onMakeDynamic={() => { setMenuOpen(null); makeDynamic(p.id); }}
                                baseRoute={baseRoute} />
                            )}
                          </div>
                          {/* Delete */}
                          <button onClick={() => setConfirmDeleteId(p.id)} title="Delete" className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition">
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="py-16 text-center text-gray-400">
            <FileText size={40} className="mx-auto mb-3 opacity-30" />
            <p>No {typeFilter !== 'all' ? typeFilter + 's' : 'posts'} found.</p>
            <Link to={`${baseRoute}/new`} className="mt-3 inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-700 text-sm font-medium"><Plus size={14} />Create the first one</Link>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between text-xs sm:text-sm text-gray-600 dark:text-gray-400">
          <span>{(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length}</span>
          <div className="flex gap-1.5">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
              className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700">← Prev</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(n => n === 1 || n === totalPages || Math.abs(n - currentPage) <= 1)
              .reduce<(number|'…')[]>((acc, n, idx, arr) => {
                if (idx > 0 && n - (arr[idx-1] as number) > 1) acc.push('…');
                acc.push(n); return acc;
              }, [])
              .map((item, idx) => item === '…'
                ? <span key={`e-${idx}`} className="px-2 py-1.5">…</span>
                : <button key={item} onClick={() => setPage(item as number)}
                    className={`px-3 py-1.5 rounded-lg border transition-colors ${currentPage === item ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>{item}</button>
              )}
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
              className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700">Next →</button>
          </div>
        </div>
      )}
    </div>
  );
}
